import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, ExternalLink, Loader2, Share2, CheckCircle } from "lucide-react";
import { MoneyWithEquivalents, buildComparisonColumns } from "@/components/QuoteComparisonCards";
import { PRODUCTION_DOMAIN } from "@/lib/constants";

interface QuoteComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quotes: any[];
  participantOptions?: { id: string; label: string; quantity: number }[];
  lead?: any;
  leadId: string;
  currency: string;
  acceptedQuotationId?: string | null;
  onSetAccepted?: (quoteId: string, quoteNumber: string) => void;
}

export const QuoteComparisonDialog = ({
  open,
  onOpenChange,
  quotes,
  participantOptions,
  lead,
  leadId,
  currency,
  acceptedQuotationId,
  onSetAccepted,
}: QuoteComparisonDialogProps) => {
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const columns = useMemo(
    () => buildComparisonColumns(quotes, { participantOptions, lead }),
    [quotes, participantOptions, lead]
  );

  const cheapestTotal = Math.min(...columns.map((c) => c.total).filter((t) => t > 0));
  const cheapestPerPerson = Math.min(
    ...columns.map((c) => c.perPerson ?? Infinity)
  );

  const allHotelNames = useMemo(() => {
    const seen = new Map<string, string>();
    columns.forEach((c) =>
      c.hotels.forEach((h) => {
        if (!seen.has(h.name.toLowerCase())) seen.set(h.name.toLowerCase(), h.name);
      })
    );
    return Array.from(seen.entries());
  }, [columns]);

  const shareMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await (supabase as any)
        .from("quote_comparisons")
        .insert({
          lead_id: leadId,
          quotation_ids: quotes.map((q) => q.id),
          title: lead?.title || null,
          created_by: user?.id ?? null,
        })
        .select("share_token")
        .single();
      if (error) throw error;
      return `${PRODUCTION_DOMAIN}/compare/${data.share_token}`;
    },
    onSuccess: (url) => {
      setShareUrl(url);
      navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Comparison link created and copied");
    },
    onError: (err: any) => toast.error(err.message || "Could not create the link"),
  });

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <tr className="border-b last:border-b-0 align-top">
      <th className="text-left text-xs font-medium text-muted-foreground py-2 pr-4 w-40 whitespace-nowrap">
        {label}
      </th>
      {children}
    </tr>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b flex-shrink-0">
          <DialogTitle>Compare quote options</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {columns.length} option{columns.length === 1 ? "" : "s"} side by side
            {lead?.client_name ? ` — ${lead.client_name}` : ""}
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
          <table className="w-full text-sm border-collapse">
            <tbody>
              <Row label="Option">
                {columns.map((c) => (
                  <td key={c.quote.id} className="py-2 px-3 min-w-[200px]">
                    <div className="font-medium">{c.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      #{c.quote.quote_number}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {acceptedQuotationId === c.quote.id && (
                        <Badge className="bg-green-600 text-white">Accepted</Badge>
                      )}
                      {c.option && (
                        <Badge variant="secondary">{c.option.label}</Badge>
                      )}
                    </div>
                  </td>
                ))}
              </Row>

              <Row label="Guests">
                {columns.map((c) => (
                  <td key={c.quote.id} className="py-2 px-3">
                    {c.pax ? `${c.pax} pax` : "—"}
                    {Number(c.quote.group_leader_count) > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {" "}+ {c.quote.group_leader_count} leader
                      </span>
                    )}
                  </td>
                ))}
              </Row>

              <Row label="Duration">
                {columns.map((c) => (
                  <td key={c.quote.id} className="py-2 px-3">
                    {c.nights > 0 ? `${c.nights} night${c.nights === 1 ? "" : "s"}` : "—"}
                  </td>
                ))}
              </Row>

              <Row label="Total">
                {columns.map((c) => (
                  <td key={c.quote.id} className="py-2 px-3">
                    <span
                      className={
                        c.total === cheapestTotal
                          ? "font-semibold text-green-600"
                          : "font-semibold"
                      }
                    >
                      <MoneyWithEquivalents amount={c.total} currency={currency} />
                    </span>
                    {c.total === cheapestTotal && columns.length > 1 && (
                      <span className="ml-1 text-xs text-green-600">lowest</span>
                    )}
                  </td>
                ))}
              </Row>

              <Row label="Per person sharing">
                {columns.map((c) => (
                  <td key={c.quote.id} className="py-2 px-3">
                    {c.perPerson != null ? (
                      <span
                        className={
                          c.perPerson === cheapestPerPerson ? "font-medium text-green-600" : ""
                        }
                      >
                        <MoneyWithEquivalents amount={c.perPerson} currency={currency} />
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                ))}
              </Row>

              <Row label="Single supplement">
                {columns.map((c) => (
                  <td key={c.quote.id} className="py-2 px-3">
                    {c.supplement > 0 ? (
                      <MoneyWithEquivalents amount={c.supplement} currency={currency} />
                    ) : (
                      "—"
                    )}
                  </td>
                ))}
              </Row>

              {allHotelNames.map(([key, name]) => {
                const present = columns.map((c) =>
                  c.hotels.find((h) => h.name.toLowerCase() === key)
                );
                const differs = present.some((h) => !h);
                return (
                  <tr key={key} className={`border-b align-top ${differs ? "bg-amber-500/5" : ""}`}>
                    <th className="text-left text-xs font-medium text-muted-foreground py-2 pr-4 w-40">
                      {name}
                    </th>
                    {present.map((h, idx) => (
                      <td key={columns[idx].quote.id} className="py-2 px-3">
                        {h ? (
                          <>
                            <span>{h.nights > 0 ? `${h.nights} night${h.nights === 1 ? "" : "s"}` : "included"}</span>
                            {h.city && (
                              <span className="text-xs text-muted-foreground"> · {h.city}</span>
                            )}
                            {h.roomType && (
                              <div className="text-xs text-muted-foreground">{h.roomType}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}

              <Row label="Optional extras">
                {columns.map((c) => (
                  <td key={c.quote.id} className="py-2 px-3">
                    {c.extras.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {c.extras.map((e, i) => (
                          <li key={i} className="text-xs">
                            {e.description} —{" "}
                            <MoneyWithEquivalents
                              amount={e.price_per_person}
                              currency={currency}
                              className="font-medium"
                              equivalentsClassName="text-[11px]"
                            />
                            <span className="text-[11px] text-muted-foreground">per person</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                ))}
              </Row>

              <Row label="Actions">
                {columns.map((c) => (
                  <td key={c.quote.id} className="py-2 px-3">
                    <div className="flex flex-col gap-1 items-start">
                      {c.quote.share_token && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              window.open(`/quote/${c.quote.share_token}`, "_blank")
                            }
                          >
                            <ExternalLink className="h-3 w-3 mr-1" /> Open quote
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                `${PRODUCTION_DOMAIN}/quote/${c.quote.share_token}`
                              );
                              toast.success(`Link copied for ${c.quote.quote_number}`);
                            }}
                          >
                            <Copy className="h-3 w-3 mr-1" /> Copy link
                          </Button>
                        </>
                      )}
                      {onSetAccepted && acceptedQuotationId !== c.quote.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => onSetAccepted(c.quote.id, c.quote.quote_number)}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" /> Mark accepted
                        </Button>
                      )}
                    </div>
                  </td>
                ))}
              </Row>
            </tbody>
          </table>
        </div>

        <div className="border-t px-6 py-4 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="text-xs text-muted-foreground truncate">
            {shareUrl ? (
              <span className="flex items-center gap-2">
                <span className="truncate">{shareUrl}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    toast.success("Link copied");
                  }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </span>
            ) : (
              "Client page shows names, hotels, nights, totals, per person and extras only."
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => shareMutation.mutate()}
              disabled={shareMutation.isPending}
            >
              {shareMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4 mr-2" />
              )}
              Share these options
            </Button>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
